import { Injectable } from "@angular/core";
import { FetchClassificatorsService, ObservationTypes, UnitValues } from "./fetch-classificators.service";
import { ObservationData } from "../models/observation.model";

@Injectable({
  providedIn: "root",
})
export class ValueTransformService {
  mmolFactor = 0.0555; // mg/dL in mmol/L conversion factor
  kgFactor = 1000;     // g to kg conversion factor
  private observationTypes: ObservationTypes;
  private unitValues: UnitValues;

  constructor(private classificatorService: FetchClassificatorsService) {
    this.classificatorService.observationTypes.subscribe((data) => this.observationTypes = data);
    this.classificatorService.unitValues.subscribe((data) => this.unitValues = data);
  }

  transform(obs: ObservationData, transformType: string): any {
    switch (transformType) {
      case "pefOneDecimal":
        return this.roundPEFOneDecimal(obs);
      case "toMmolValue":
        return this.convertToMmol(obs);
      case "mgdlOneDecimal":
        return this.convertMgDlToOneDecimal(obs);
      case "weightOneDecimal":
        return this.convertWeightToOneDecimal(obs);
      case "BMIOneDecimal":
        return this.convertBMIOneDecimal(obs);
      case "oneDecimalHeight":
        return this.getOneDecimalHeight(obs);
      case "oneDecimalWaist":
        return this.getOneDecimalWaist(obs);
      case "BMIcircleLocation":
        return this.calculateBMIcircleLocation(obs);
      default:
        return "--";
    }
  }

  private roundPEFOneDecimal(obs: ObservationData): number {
    let obsValue = parseFloat(obs.observationList.find(elem => elem.classificatorCodeId == this.observationTypes.PEF)?.value || "0");
    return Math.round(obsValue * 10) / 10;
  }

  private convertToMmol(obs: ObservationData): number {
    return obs.observationList.reduce((acc, cur) => Math.round(parseFloat(cur.value) * this.mmolFactor * 10) / 10, NaN);
  }

  private convertMgDlToOneDecimal(obs: ObservationData): number {
    return obs.observationList.reduce((acc, cur) => Math.round(parseFloat(cur.value) / this.mmolFactor * 10) / 10, NaN);
  }

  private convertWeightToOneDecimal(obs: ObservationData) {
    let value = obs.observationList.reduce((acc, cur) => {
      if (cur.valueUnitId === this.unitValues.kg) {
        return Math.round(parseFloat(cur.value) * 10) / 10;
      } else if (cur.valueUnitId === this.unitValues.g) {
        return Math.round((parseFloat(cur.value) / this.kgFactor) * 10) / 10;
      }
      return acc;
    }, NaN);
    return isNaN(value) ? "--" : value;
  }

  private convertBMIOneDecimal(obs: ObservationData) {
    let bmi = obs.observationList.find((obs) => obs.classificatorCodeId == this.observationTypes.BMI);
    return bmi ? Math.round(parseFloat(bmi.value) * 10) / 10 : "--";
  }

  private getOneDecimalHeight(obs: ObservationData) {
    let match = obs.observationList.find((child) => child.classificatorCodeId === this.observationTypes.height);
    if (match && match.value && match.valueUnitId == this.unitValues.cm) {
      return Math.round(parseFloat(match.value) * 10) / 10;
    } else {
      return "--";
    }
  }

  private getOneDecimalWaist(obs: ObservationData) {
    let match = obs.observationList.find((child) => child.classificatorCodeId === this.observationTypes.waist);
    if (match && match.value && match.valueUnitId == this.unitValues.cm) {
      return Math.round(parseFloat(match.value) * 10) / 10;
    } else {
      return "--";
    }
  }

  private calculateBMIcircleLocation(obs: ObservationData) {
    let BMIscaleMin = 15;
    let BMIscaleMax = 40;
    const value = parseFloat(obs.value);
    let parentDivWidth = 0.7; // 70%
    let result = Math.round((value - BMIscaleMin) * 100 / (BMIscaleMax - BMIscaleMin) * parentDivWidth);
    return (value > BMIscaleMax) ? Math.round(100 * parentDivWidth) : result;
  }
}
